import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FaBuilding,
  FaEnvelope,
  FaEye,
  FaEyeSlash,
  FaLock,
} from "react-icons/fa";
import api from "../services/api";
import StorageService from "../services/StorageService";
import AuthPageLogo from "../components/AuthPageLogo";
import FounderAccessHeroPanel from "../components/auth/FounderAccessHeroPanel";
import "../assets/styles/login-page.scss";

export default function ActivateLogisticsProviderPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token") || "";

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [invitation, setInvitation] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [activated, setActivated] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("Activation token is missing from this link.");
      setLoading(false);
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/auth/logistics-provider-activation", { params: { token } });
        if (cancelled) return;
        const preview = res.data?.data?.invitation || null;
        setInvitation(preview);
        if (preview?.alreadyAccepted) setActivated(true);
      } catch (error) {
        if (!cancelled) {
          setLoadError(
            error?.response?.data?.message ||
              error?.response?.data?.error ||
              "This activation link is invalid or has expired.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [token]);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      toast.error("Activation token is missing.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match.");
      return;
    }

    setSaving(true);
    try {
      const res = await api.post("/auth/logistics-provider-activation/accept", {
        token,
        password,
        confirmPassword,
      });
      const body = res.data?.data || {};
      if (body.token) {
        StorageService.setAuth(
          { token: body.token, user: body.user || {} },
          { remember: false, cookieDays: 1 },
        );
      }
      setActivated(true);
      toast.success("Account activated. Please complete your Logistics Provider Profile.");
      navigate(body.redirectPath || "/logistics-profile/edit", { replace: true });
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Failed to activate account.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="login-page login-page--split">
      <div className="login-page__shell login-page__shell--split">
        <FounderAccessHeroPanel />
        <main className="login-page__form-panel login-page__form-panel--split">
          <div className="login-page__form-inner">
            <div className="login-page__form-logo">
              <AuthPageLogo />
            </div>
            <h2 className="login-page__form-title">
              {activated ? "Account activated" : "Activate your logistics provider account"}
            </h2>
            <p className="login-page__form-subtitle">
              {activated
                ? "Continue to your Logistics Provider Profile to finish setup."
                : "Create a password to activate your account"}
            </p>

            {loading ? (
              <div className="text-center py-4 text-muted">Loading activation details…</div>
            ) : activated ? (
              <div className="text-center py-2">
                <Link to="/logistics-profile/edit" className="login-page__submit">
                  Go to Logistics Provider Profile
                </Link>
              </div>
            ) : loadError || !invitation ? (
              <div className="text-center py-3">
                <p className="text-muted mb-3">{loadError || "Invalid activation link."}</p>
                <Link to="/login" className="login-page__submit">
                  Go to login
                </Link>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate>
                <div className="login-page__field">
                  <label className="login-page__label" htmlFor="lp-activate-email">
                    Login email
                  </label>
                  <div className="login-page__input-wrap">
                    <FaEnvelope className="login-page__input-icon" aria-hidden />
                    <input
                      id="lp-activate-email"
                      className="login-page__input login-page__input--tinted"
                      value={invitation.email}
                      disabled
                    />
                  </div>
                </div>
                <div className="login-page__field">
                  <label className="login-page__label" htmlFor="lp-activate-company">
                    Company
                  </label>
                  <div className="login-page__input-wrap">
                    <FaBuilding className="login-page__input-icon" aria-hidden />
                    <input
                      id="lp-activate-company"
                      className="login-page__input login-page__input--tinted"
                      value={invitation.companyName}
                      disabled
                    />
                  </div>
                </div>
                <div className="login-page__field">
                  <label className="login-page__label" htmlFor="lp-activate-password">
                    Password
                  </label>
                  <div className="login-page__input-wrap">
                    <FaLock className="login-page__input-icon" aria-hidden />
                    <input
                      id="lp-activate-password"
                      type={showPassword ? "text" : "password"}
                      className="login-page__input login-page__input--tinted"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="login-page__input-toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <div className="login-page__field">
                  <label className="login-page__label" htmlFor="lp-activate-confirm">
                    Confirm password
                  </label>
                  <div className="login-page__input-wrap">
                    <FaLock className="login-page__input-icon" aria-hidden />
                    <input
                      id="lp-activate-confirm"
                      type={showConfirmPassword ? "text" : "password"}
                      className="login-page__input login-page__input--tinted"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      autoComplete="new-password"
                      required
                    />
                    <button
                      type="button"
                      className="login-page__input-toggle"
                      onClick={() => setShowConfirmPassword((v) => !v)}
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                <button type="submit" className="login-page__submit" disabled={saving}>
                  {saving ? "Activating…" : "Activate Account"}
                </button>
              </form>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
